import React from 'react';
import { Card, CardBody, Badge, Input, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Paperclip, Calendar, CheckCircle2, Plus, Lock } from 'lucide-react';

const NoteCard = ({ note, isSelected, onSelect, onClick, onFileUpload, uploading, viewMode = 'grid' }) => {
    const { t } = useTranslation();

    const hasAttachments = note.hasAttachments || (note.attachments && note.attachments.length > 0);
    const attachmentCount = note.attachments ? note.attachments.length : (note.hasAttachments ? '?' : 0);

    const renderConfidentialBadge = () => note.confidential ? (
        <Badge
            color="secondary"
            className="rounded-pill shadow-sm d-inline-flex align-items-center gap-1 px-2 py-1"
            style={{ fontSize: '0.7rem' }}
            title={t('confidential_content_hidden')}
        >
            <Lock size={11} />
            <span>{t('confidential')}</span>
        </Badge>
    ) : null;

    const renderAttachments = () => (
        <div className="d-flex align-items-center gap-2">
            {renderConfidentialBadge()}
            {hasAttachments ? (
                <Badge
                    color="warning"
                    className="text-dark rounded-pill shadow-sm d-inline-flex align-items-center gap-1 px-2 py-1"
                    style={{ fontSize: '0.72rem' }}
                >
                    <Paperclip size={12} />
                    {attachmentCount !== '?' && <span>{attachmentCount}</span>}
                </Badge>
            ) : null}
        </div>
    );

    const renderUpload = () => onFileUpload && (
        <div onClick={e => e.stopPropagation()}>
            <input 
                type="file" 
                id={`file-${note.id}`} 
                className="d-none" 
                multiple 
                onChange={e => onFileUpload(note.id, Array.from(e.target.files))} 
            />
            <label 
                htmlFor={`file-${note.id}`} 
                className="btn btn-sm btn-outline-primary rounded-pill mb-0 px-2 py-1 fw-bold d-inline-flex align-items-center gap-1"
                style={{ cursor: 'pointer', fontSize: '0.72rem' }}
                title={t('add_attachment')}
            >
                {uploading === note.id ? (
                    <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                ) : (
                    <>
                        <Plus size={12} />
                        <Paperclip size={12} />
                    </>
                )}
            </label>
        </div>
    );

    const renderTargetDate = () => {
        if (!note.targetDate) return null;
        const target = new Date(note.targetDate);
        const now = new Date();
        const isOverdue = target < now;
        
        if (note.completed) {
            return (
                <div className="d-flex align-items-center gap-1">
                    <Badge 
                        color="light" 
                        className="text-secondary border rounded-pill shadow-sm d-inline-flex align-items-center gap-1 px-2 py-1" 
                        style={{ fontSize: '0.7rem' }}
                    >
                        <Calendar size={12} />
                        <span>{target.toLocaleString()}</span>
                    </Badge>
                    <span className="text-success d-inline-flex align-items-center" title={t('completed')}>
                        <CheckCircle2 size={16} />
                    </span>
                </div>
            );
        }

        return (
            <Badge 
                color={isOverdue ? "danger" : "success"} 
                className="rounded-pill shadow-sm d-inline-flex align-items-center gap-1 px-2 py-1" 
                style={{ fontSize: '0.7rem' }}
            >
                <Calendar size={12} />
                <span>{target.toLocaleString()}</span>
            </Badge>
        );
    };

    if (viewMode === 'list') {
        return (
            <Card 
                className={`mb-2 transition-all ${isSelected ? 'ring-primary border-primary' : ''}`} 
                style={{ cursor: 'pointer', overflow: 'hidden' }}
                onClick={() => onClick(note.id)}
            >
                <div className="d-flex align-items-center p-2 px-3 gap-3">
                    <div onClick={e => e.stopPropagation()}>
                        <Input 
                            type="checkbox" 
                            checked={isSelected} 
                            onChange={() => onSelect(note.id)}
                            style={{ width: '18px', height: '18px', marginTop: 0 }}
                        />
                    </div>
                    <div className="flex-grow-1 min-width-0">
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <h6 className="mb-0 text-truncate fw-bold">
                                {note.title || <span className="text-muted italic small">{t('no_title')}</span>}
                            </h6>
                            <small className="text-muted flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                {note.updatedAt ? new Date(note.updatedAt).toLocaleDateString() : ''}
                            </small>
                        </div>
                        {!note.confidential && (
                            <div className="text-muted text-truncate" style={{ fontSize: '0.85rem' }}>
                                {note.content}
                            </div>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {renderTargetDate()}
                        {renderAttachments()}
                        {renderUpload()}
                    </div>
                </div>
            </Card>
        );
    }

    if (viewMode === 'small') {
        return (
            <Card 
                className={`h-100 transition-all ${isSelected ? 'ring-primary border-primary' : ''}`} 
                style={{ cursor: 'pointer', overflow: 'hidden' }}
                onClick={() => onClick(note.id)}
            >
                <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div onClick={e => e.stopPropagation()}>
                            <Input 
                                type="checkbox" 
                                checked={isSelected} 
                                onChange={() => onSelect(note.id)}
                                style={{ width: '16px', height: '16px' }}
                            />
                        </div>
                        {renderAttachments()}
                    </div>
                    <h6 className="card-title fw-bold text-truncate mb-1">
                        {note.title || <span className="text-muted italic small">{t('no_title')}</span>}
                    </h6>
                    {!note.confidential && (
                        <p className="card-text text-secondary mb-0 text-truncate-2" style={{ 
                            fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>
                            {note.content}
                        </p>
                    )}
                    {note.targetDate && (
                        <div className="mt-2">
                            {renderTargetDate()}
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }

    // Default Grid (Large Tiles)
    return (
        <Card 
            className={`h-100 transition-all ${isSelected ? 'ring-primary border-primary' : ''}`} 
            style={{ cursor: 'pointer', overflow: 'hidden' }}
            onClick={() => onClick(note.id)}
        >
            <div className="p-3 pb-0 d-flex justify-content-between align-items-start">
                <div onClick={e => e.stopPropagation()}>
                    <Input 
                        type="checkbox" 
                        checked={isSelected} 
                        onChange={() => onSelect(note.id)}
                        style={{ width: '20px', height: '20px' }}
                    />
                </div>
                <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                    {note.updatedAt ? new Date(note.updatedAt).toLocaleDateString() : ''}
                </small>
            </div>
            <CardBody className="pt-2">
                <h5 className="card-title fw-bold mb-2">
                    {note.title || <span className="text-muted italic small">{t('no_title')}</span>}
                </h5>
                {!note.confidential && (
                    <p className="card-text text-secondary mb-3" style={{ 
                        display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden', fontSize: '0.9rem'
                    }}>
                        {note.content}
                    </p>
                )}
                <div className="d-flex flex-wrap gap-1">
                    {(note.tags || []).slice(0, 3).map(tag => (
                        <Badge key={tag} color="light" className="text-primary border border-primary-subtle rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                            #{tag}
                        </Badge>
                    ))}
                    {(note.tags || []).length > 3 && <small className="text-muted">+{note.tags.length - 3}</small>}
                </div>
            </CardBody>
            <div className="card-footer bg-light bg-opacity-10 border-0 d-flex justify-content-between align-items-center py-2 px-3">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    {renderTargetDate()}
                    {renderAttachments()}
                </div>
                {renderUpload()}
            </div>
        </Card>
    );
};

export default NoteCard;

